import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Modal,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { ContentCopyRounded } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import UserPrompt from 'shared/components/UserPrompt';
import { useNotification } from 'shared/hooks';
import { copyLinkToClipboard } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { CardTabs } from 'shared/components/styled/tabs';

/*
Additional reusable and interactable components should be moved here
 */

/**
 * @param {Object} props
 * @param {Array<Object>} props.options
 * @param {string} props.options[].name
 * @param {*} props.options[].value
 * @param {Function} props.setValue
 * @param {*} props.value
 */
export function ToggleSelect({ options, setValue, value }) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(e, v) => setValue(v)}
      color={BUTTON_COLORS.primary}
    >
      {options.map(({ name, value: optionValue }) => (
        <ToggleButton key={name} value={optionValue} sx={{ padding: '5px' }} disabled={optionValue === value}>
          {name}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/**
 * @param {Object} props
 * @param {string|Object} props.message
 * @param {Object} props.icon
 * @param {string} [props.placement='right']
 */
export function TooltipIcon({
  message,
  icon: Icon,
  placement = 'right',
  ...rest
}) {
  return (
    <Tooltip title={message} arrow placement={placement}>
      <Icon {...rest} />
    </Tooltip>
  );
}

/**
 * @param {Object} props
 * @param {string} props.message
 * @param {Function} props.onClick
 * @param {Object} props.icon
 * @param {string} [props.color]
 * @param {string} [props.placement='right']
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.disabledTip='Unavailable']
 * @param {string} [props.text]
 */
export function TooltipIconButton({
  message,
  onClick,
  icon: Icon,
  color = undefined,
  placement = 'right',
  disabledTip = 'Unavailable',
  disabled = false,
  text = undefined,
  ...rest
}) {
  return (
    <Tooltip title={disabled ? disabledTip : message} arrow placement={placement}>
      {/* span is necessary to display tooltip message when button is disabled */}
      <span>
        {!text ? (
          <IconButton
            onClick={onClick}
            color={color}
            // The Tooltip labels its child (the span), not the button.
            aria-label={message}
            {...rest}
            disabled={disabled}
          >
            <Icon />
          </IconButton>
        ) : (
          <Button
            onClick={onClick}
            color={color}
            {...rest}
            disabled={disabled}
            startIcon={<Icon />}
          >
            {text}
          </Button>
        )}
      </span>
    </Tooltip>
  );
}

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {string} [props.titleLink='']
 * @param {string} [props.subtitle='']
 * @param {Object} [props.titleObject=null]
 */
export function Title(props) {
  const {
    title,
    titleLink = '',
    subtitle = '',
    titleObject = null,
    children = null,
  } = props;

  const { pushInfo } = useNotification();

  let fullTitle = title;
  let headerValue = title;

  if (subtitle) {
    fullTitle = `${title} - ${subtitle}`;
    headerValue = (
      <>
        <Link to={titleLink}>{title}</Link>
        {` - ${subtitle}`}
      </>
    );
  }

  if (titleObject !== null) {
    headerValue = titleObject;
  }

  useEffect(() => {
    if (fullTitle) {
      window.document.title = fullTitle;
    }
  }, [fullTitle]);

  return (
    <Grid
      container
      sx={{
        alignItems: 'center',
        justifyContent: 'start',
        wrap: 'nowrap',
        marginBottom: 2,
      }}
      size={12}
      spacing={1}
    >
      <Grid>
        {/* noWrap hides overflow; the padding keeps title links' focus rings whole. */}
        <Typography variant="h5" noWrap sx={{ p: '4px', m: '-4px' }}>
          {headerValue}
        </Typography>
      </Grid>
      {children && <Grid>{children}</Grid>}
      <Grid>
        <ContentCopyRounded
          className="clickable-icon"
          onClick={() => {
            copyLinkToClipboard(window.location, fullTitle)
              .then(() => pushInfo('Copied hyperlink to clipboard'));
          }}
        />
      </Grid>
    </Grid>
  );
}

/**
 * Returns a link that will open a new tab when clicked.
 * @param {Object} props
 * @param {string} props.link
 * @param {string} [props.text='']
 * @param {string} [props.className='']
 */
export function NewTabLink({ link, text = '', className = '' }) {
  return (
    <a
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      href={link}
    >
      {text || link}
    </a>
  );
}

/**
 * InfoModal renders a button that opens an mui modal.
 * If actionCallback is provided, an additional button is rendered.
 * @param {Object} props
 * @param {React.ReactNode} props.description
 * @param {string} [props.title='Information']
 * @param {string} [props.actionName='Information']
 * @param {Function} [props.actionCallback=null]
 * @param {string} [props.className='']
 * @param {string} [props.closeText='Close']
 * @param {string} [props.color] - One of: 'primary', 'secondary', 'error', 'warning',
 *   'info', 'success'. Colours the confirm button as well as the trigger, so a destructive
 *   action passes 'error'.
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.placement='left']
 * @param {string} [props.disabledTip='Unavailable']
 * @param {Object} [props.icon=null]
 * @param {string} [props.tip='']
 */
export function InfoModal(props) {
  const {
    title = 'Information',
    actionName = 'Information',
    actionCallback,
    description,
    className,
    closeText = 'Close',
    color = BUTTON_COLORS.primary,
    disabled,
    placement = 'left',
    icon: Icon,
    disabledTip = 'Unavailable',
    tip,
  } = props;

  const [show, setShow] = useState(false);

  return (
    <>
      { Icon
        ? (
          <TooltipIconButton
            message={disabled ? disabledTip : tip}
            disabledTip={disabledTip}
            icon={Icon}
            /* Setting to !disabled ensures that modal only works if disabled == false */
            onClick={() => setShow(!disabled)}
            disabled={disabled}
            className={className}
            placement={placement}
          />
        )
        : (
          <Tooltip title={disabled ? disabledTip : tip}>
            <span>
              <Button
                className={className}
                variant="contained"
                color={color}
                onClick={() => setShow(true)}
                disabled={disabled}
              >
                {actionName}
              </Button>
            </span>
          </Tooltip>
        )}
      <Modal
        open={show}
        onClose={() => setShow(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Grid container className="modal-style" spacing={1}>
          <Grid size={12}>
            <Typography id="modal-modal-title" variant="h6" component="h2">{title}</Typography>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid id="modal-modal-description" size={12}>
            <Typography>{description}</Typography>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid size={12} container sx={{ justifyContent: 'end' }}>
            <Grid>
              <Button
                variant="outlined"
                onClick={() => setShow(false)}
                sx={{ marginRight: '10px' }}
              >
                {closeText}
              </Button>
            </Grid>
            {actionCallback
              && (
                <Grid>
                  <Button
                    variant={VARIANTS.contained}
                    color={color}
                    onClick={() => {
                      setShow(false);
                      actionCallback();
                    }}
                  >
                    {actionName}
                  </Button>
                </Grid>
              )}
          </Grid>
        </Grid>
      </Modal>
    </>
  );
}

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {number} props.index
 * @param {number} props.value
 */
function TabPanel(props) {
  const {
    children, value, index, ...other
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Grid container sx={{ justifyContent: 'space-between', padding: '10px 30px' }}>
            {children}
        </Grid>
      )}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.dirty
 * @param {string|Object} props.title
 * @param {Array<*>} props.optionsContentArray
 * @param {Object} [props.titleAction=null]
 */
export function TabbedCard({
  dirty, title, titleAction = null, optionsContentArray,
  id, type,
}) {
  const [tab, setTab] = useState(0);
  const [prompt, setPrompt] = useState(false);
  const [onAccept, setOnAccept] = useState(() => null);

  const changeTab = (value) => setTab(value);

  const onChange = (event, newValue) => {
    if (dirty) {
      const accept = () => () => {
        changeTab(newValue);
        setPrompt(false);
      };
      setOnAccept(accept);
      setPrompt(true);
      return;
    }
    setTab(newValue);
  };

  return (
    <>
      <UserPrompt
        denyCallback={() => setPrompt(false)}
        message="Unsaved changes will be lost if you continue. Switch tabs?"
        acceptBtnText="Continue"
        acceptCallback={onAccept}
        isOpen={prompt}
        key={`${type}-user-prompt-${id}`}
      />
      <Card
        variant="outlined"
        sx={(theme) => ({
          backgroundColor: (theme.vars || theme).palette.cardBackground,
          height: '100%',
          minHeight: '32rem',
        })}
        key={`${type}-${id}`}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            px: '20px',
            py: '15px',
            height: 100,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              placeSelf: 'start',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {title}
          </Typography>
          {titleAction}
        </Box>
        <CardContent sx={{ p: 0 }}>
          <CardTabs
            variant="scrollable"
            scrollButtons="auto"
            sx={{ marginBottom: '5px' }}
            value={tab}
            onChange={onChange}
          >
            {optionsContentArray.map(({ label, disabled }) => (
              <Tab
                key={`${type}-${label}-${id}`}
                label={label}
                disabled={disabled}
              />
            ))}
          </CardTabs>
          {optionsContentArray.map(({ content, label }, index) => (
            <TabPanel key={`tab-panel-${label}`} index={tab} value={index}>
              {content}
            </TabPanel>
          ))}
        </CardContent>
      </Card>
    </>
  );
}

/**
 * @param {Object} props
 * @param {Array<*>} props.links
 * @param {string} props.value
 * @param {string} props.label
 */
export function ModuleNav({ links, value, label }) {
  return (
    <CardTabs
      value={value}
      aria-label={label}
      role="navigation"
      scrollButtons="auto"
      variant="scrollable"
    >
      {links}
    </CardTabs>
  );
}
